import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { useStartSubscriptionView } from "@/hooks/useStartSubscriptionView";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useToast } from "@/hooks/useToast";
import { createCreditCheckout } from "@/server/services/creditsService";
import type { CreditBalanceResponse } from "@/server/types/subscription";
import BalanceDisplay from "./credits/BalanceDisplay";
import CreditSelector from "./credits/CreditSelector";
import NoSubscriptionGate from "./credits/NoSubscriptionGate";
import StrandedCreditsPanel from "./credits/StrandedCreditsPanel";
import UsageHint from "./credits/UsageHint";
import { safeSession } from "@/utils/safeStorage";
import {
  MOCK_BALANCES,
  PRICE_PER_CREDIT,
  fmtPrice,
  snapAmount,
  type BalanceData,
} from "./credits/creditsConfig";

function mapToBalanceData(raw: CreditBalanceResponse): BalanceData {
  return {
    addon: Math.floor(raw.availableCredits),
    planUsed: Math.round(raw.currentPeriodSpendCredits),
    planTotal: raw.planCredits,
  };
}

export default function AddonCreditsView() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const cancelPaymentsView = useNavigationState((s) => s.cancelPaymentsView);
  const plan = useSubscriptionState((s) => s.subscription.plan);
  const status = useSubscriptionState((s) => s.subscription.status);
  const startSubscriptionView = useStartSubscriptionView();

  const [containerWidth, setContainerWidth] = useState(1200);
  const isCompact = containerWidth < 700;
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = wrapRef.current?.parentElement ?? wrapRef.current;
    if (!target) {
      return;
    }
    const measure = () =>
      setContainerWidth(target.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  const isSubscriber = status === "active" || status === "trialing";

  // Fetch for ALL users (not just subscribers) so a free / churned user can
  // still see add-on credits stranded behind a lapsed subscription.
  const {
    data: rawBalance,
    isLoading: balanceLoading,
    isError: balanceError,
  } = useCreditBalance();
  const balance: BalanceData = rawBalance
    ? mapToBalanceData(rawBalance)
    : (MOCK_BALANCES[plan] ?? MOCK_BALANCES.standard);
  const periodEnd = rawBalance?.currentPeriodEnd ?? null;

  // Stranded add-on credits: a non-subscriber holding a frozen balance. We
  // surface a read-only "credits waiting" panel instead of the generic gate —
  // but the buy/purchase action stays subscriber-only. Defensive on absent
  // addOns (old BE): fall back to availableCredits and the !isSubscriber gate.
  const strandedBalance =
    rawBalance?.addOns?.balance ?? rawBalance?.availableCredits ?? 0;
  // Gate on the floored, whole-credit value the panel actually displays (and
  // guard non-finite values from a malformed DTO). A fractional balance in
  // (0,1) would otherwise show the panel reading "0 credits waiting".
  const wholeStrandedCredits = Number.isFinite(strandedBalance)
    ? Math.floor(strandedBalance)
    : 0;
  const hasStrandedCredits =
    !isSubscriber &&
    rawBalance?.addOns?.spendable === false &&
    wholeStrandedCredits >= 1;

  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(50);

  const handleBuy = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = await createCreditCheckout(amount);
      safeSession.set(
        "credits_pre_purchase_balance",
        String(Math.floor(rawBalance?.availableCredits ?? 0)),
      );
      window.location.href = url;
    } catch {
      setIsLoading(false);
      toast({
        title: t("payments.checkoutError", "Something went wrong"),
        description: t(
          "payments.checkoutErrorDescription",
          "Unable to start checkout. Please try again.",
        ),
        variant: "destructive",
      });
    }
  }, [amount, rawBalance, t, toast]);

  const buyLabel = isLoading
    ? t("payments.processing", "Processing…")
    : t("payments.buyCredits", {
        amount: amount.toLocaleString(),
        price: fmtPrice(amount * PRICE_PER_CREDIT),
      });

  const handleAmountChange = useCallback(
    (n: number) => setAmount(snapAmount(n)),
    [],
  );

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 flex size-full flex-col overflow-hidden bg-background lg:border-l lg:border-border"
    >
      {/* Header */}
      {isCompact ? (
        <div className="grid grid-cols-[32px_1fr_32px] items-center gap-2 border-b border-border bg-card/60 px-3.5 py-2.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelPaymentsView}
            className="size-8"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-center text-[14px] font-semibold">
            {t("payments.addonCredits")}
          </span>
          <span />
        </div>
      ) : (
        // Back control renders here too: below Tailwind lg (960px) the view
        // still covers the left column, so a breadcrumb-only header strands
        // 700–959px users (docs/settings-middle-views/design.md).
        <div className="flex h-leftColumnHeaderHeight items-center gap-2 border-b border-border px-3.5 lg:px-5">
          {/* Visible only below lg — the 700-959px band where the view still
              covers the left column. Hidden at lg+ (left column visible). */}
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelPaymentsView}
            className="size-8 lg:hidden"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-sm text-muted-foreground">
            {t("settings")} / {t("payments.title")} /{" "}
            {t("payments.addonCredits")}
          </span>
        </div>
      )}

      {/* Body */}
      {!isSubscriber ? (
        <div className="flex-1 overflow-y-auto">
          {hasStrandedCredits ? (
            <StrandedCreditsPanel
              balance={strandedBalance}
              onResubscribe={startSubscriptionView}
            />
          ) : (
            <NoSubscriptionGate onSubscribe={startSubscriptionView} />
          )}
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[640px] space-y-[18px] p-4 lg:max-w-[760px] lg:px-8 lg:pb-12 lg:pt-[18px]">
              <BalanceDisplay
                balance={balance}
                loading={balanceLoading}
                isError={balanceError}
                periodEnd={periodEnd}
                isCompact={isCompact}
              />

              {/* Selector card */}
              <div className="flex flex-col gap-[18px] rounded-[14px] border border-border bg-card p-4 lg:gap-[22px] lg:px-7 lg:py-8">
                <CreditSelector
                  amount={amount}
                  onChange={handleAmountChange}
                  isCompact={isCompact}
                />
                {/* Inline CTA — desktop only */}
                {!isCompact && (
                  <>
                    <Button
                      onClick={handleBuy}
                      disabled={isLoading || balanceLoading}
                      className="h-[52px] w-full gap-2 text-[16px] font-semibold"
                    >
                      {buyLabel}
                      {!isLoading && <ArrowRight size={16} />}
                    </Button>
                    <p className="-mt-3 text-center text-[11.5px] text-muted-foreground">
                      {t("payments.creditsValid")}
                    </p>
                  </>
                )}
              </div>

              {/* Footer notes */}
              <div className="flex flex-col items-start gap-2.5">
                <div className="inline-flex items-center gap-2 text-[12px] text-muted-foreground">
                  <span className="size-1 shrink-0 rounded-full bg-muted-foreground/50" />
                  <span>{t("payments.creditsMoment")}</span>
                </div>
                <UsageHint />
              </div>
            </div>
          </div>

          {/* Mobile sticky CTA — outside scroll area */}
          {isCompact && (
            <div className="bg-background/92 border-t border-border/70 px-4 pb-[calc(14px+env(safe-area-inset-bottom))] pt-3.5 backdrop-blur-md">
              <Button
                onClick={handleBuy}
                disabled={isLoading || balanceLoading}
                className="h-12 w-full gap-2 text-[15px] font-semibold"
              >
                {buyLabel}
                {!isLoading && <ArrowRight size={16} />}
              </Button>
              <p className="mt-1.5 text-center text-[11.5px] text-muted-foreground">
                {t("payments.creditsValid")}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
